import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/DeliverOrder/Cart/Cart.styled"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit,
  OrderOperationSearchHits,
  OrderModificationShippingFees,
  OrderShippingFeesModificationSearchHit
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel } from "../Shared.styled"
import { Link } from "react-router-dom"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import StartOperation from "./StartOperation"
import { ApolloQueryResult, useMutation } from "@apollo/client"
import START_SHIPPING_FEE_MODIFICATION from "../../../graphql/mutations/order/modification/shippingFees/StartModification"
import RESTART_SHIPPING_FEE_MODIFICATION from "../../../graphql/mutations/order/modification/shippingFees/RestartModification"
import { handleErrorMessages } from "helpers/errors"
import ErrorMessages from "../../Order/ManageOrder/ErrorMessages"
import SecondaryButton from "../../Ui/Buttons/SecondaryButton"
import CopyButton from "../../Ui/CopyButton"
import {
  Container,
  Title,
  ProviderWrapper,
  Provider,
  Providers,
  ActionWrapper,
  LabelWrapper,
  ButtonWrapper
} from "../../Order/ManageOrder/DeliverOrder/FailedDeliveries.styled"
import ActionInfoBox from "./ActionInfoBox"
import { ActionDropdownButton } from "../../Ui/Buttons/ActionDropdownButton"
import { ReactComponent as PaymentIcon } from "../../../images/icons/credit-card.svg"
import {
  TotalsComparison,
  TotalsHeader,
  TotalsRow,
  TotalsLabel,
  TotalsCell,
  ShippingFeesTable,
  TableHeader,
  TableBody
} from "./ShippingFeesModificationAction.styled"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  refetchOperations: () => Promise<ApolloQueryResult<OrderOperationSearchHits>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const ShippingFeesModificationAction = ({
  operation,
  order,
  orderHistory,
  getCustomCode,
  setShow,
  refetchOperations,
  setCreatedOperations,
  setFailedOperations
}: Props) => {
  const [paymentAction, setPaymentAction] =
    useState<graphql.InputActionPaymentOrderModificationShippingFees>({
      actionType: graphql.InputActionType.Auto
    })
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })

  const searchOperation = operation as OrderShippingFeesModificationSearchHit
  const searchShippingFees = searchOperation.shippingFees || []

  const modification = order?.Modifications?.find(
    (mod): mod is OrderModificationShippingFees =>
      mod.__typename === "OrderModificationShippingFees" && mod.id === operation.id
  )

  const [startShippingFeesModification, { loading }] = useMutation(
    START_SHIPPING_FEE_MODIFICATION,
    {
      onCompleted: () => {
        setShow(false)
        setCreatedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
      },
      onError: (error) => {
        handleErrorMessages(error)
      }
    }
  )

  const [restartShippingFeesModification, { loading: restartLoading }] = useMutation(
    RESTART_SHIPPING_FEE_MODIFICATION,
    {
      onCompleted: () => {
        setShow(false)
        setFailedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
        refetchOperations()
      },
      onError: (error) => {
        handleErrorMessages(error)
      }
    }
  )

  const startOperation = () => {
    startShippingFeesModification({
      variables: {
        modificationId: operation.id,
        input: {
          payment: paymentAction
        }
      }
    })
  }

  const restartOperation = () => {
    restartShippingFeesModification({
      variables: {
        modificationId: operation.id,
        input: {
          payment: paymentAction
        }
      }
    })
  }

  const isModificationFailed = modification?.failed && !modification.completed
  const hasPaymentProvider = !!modification?.paymentProvider

  const renderShippingFeesTable = () => {
    if (searchShippingFees.length === 0) return null

    return (
      <ShippingFeesTable>
        <TableHeader>
          <tr>
            <th style={{ textAlign: "left" }}>Shipping Fee ID</th>
            <th>Before Price</th>
            <th>After Price</th>
            <th>Before Discount</th>
            <th>After Discount</th>
          </tr>
        </TableHeader>
        <TableBody>
          {searchShippingFees.map((fee, index) => (
            <tr key={fee.data?.id || index}>
              <td>{fee.data?.id ?? "—"}</td>
              <td>
                {fee.prev?.price ? (
                  <Money
                    amount={fee.prev.price.salePriceAmount}
                    currencyUnit={order.currencyCode as DineroFactory.Currency}
                  />
                ) : (
                  "—"
                )}
              </td>
              <td>
                {fee.data?.price ? (
                  <Money
                    amount={fee.data.price.salePriceAmount}
                    currencyUnit={order.currencyCode as DineroFactory.Currency}
                  />
                ) : (
                  "—"
                )}
              </td>
              <td>
                {fee.prev?.price ? (
                  <Money
                    amount={fee.prev.price.discountAmount}
                    currencyUnit={order.currencyCode as DineroFactory.Currency}
                  />
                ) : (
                  "—"
                )}
              </td>
              <td>
                {fee.data?.price ? (
                  <Money
                    amount={fee.data.price.discountAmount}
                    currencyUnit={order.currencyCode as DineroFactory.Currency}
                  />
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </TableBody>
      </ShippingFeesTable>
    )
  }

  const renderTotalsComparison = () => {
    const oldTotals = modification?.oldTotals
    const newTotals = modification?.newTotals

    if (!oldTotals || !newTotals) return null

    const subTotalDiff = (newTotals.subTotal ?? 0) - (oldTotals.subTotal ?? 0)
    const discountDiff = (newTotals.discountTotal ?? 0) - (oldTotals.discountTotal ?? 0)
    const taxDiff = (newTotals.taxTotal ?? 0) - (oldTotals.taxTotal ?? 0)
    const grandTotalDiff = newTotals.grandTotal - oldTotals.grandTotal

    return (
      <TotalsComparison>
        <TotalsHeader>
          <span></span>
          <span style={{ textAlign: "right" }}>Before</span>
          <span style={{ textAlign: "right" }}>After</span>
          <span style={{ textAlign: "right" }}>Diff</span>
        </TotalsHeader>

        {oldTotals.subTotal !== undefined && newTotals.subTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Subtotal</TotalsLabel>
            <TotalsCell $type="before">
              <Money
                amount={oldTotals.subTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              <Money
                amount={newTotals.subTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {subTotalDiff !== 0 ? (
                <Money
                  amount={subTotalDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : (
                "—"
              )}
            </TotalsCell>
          </TotalsRow>
        )}

        {oldTotals.discountTotal !== undefined && newTotals.discountTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Discount</TotalsLabel>
            <TotalsCell $type="before">
              -
              <Money
                amount={oldTotals.discountTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              -
              <Money
                amount={newTotals.discountTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {discountDiff !== 0 ? (
                <Money
                  amount={discountDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : (
                "—"
              )}
            </TotalsCell>
          </TotalsRow>
        )}

        {oldTotals.taxTotal !== undefined && newTotals.taxTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Tax</TotalsLabel>
            <TotalsCell $type="before">
              <Money
                amount={oldTotals.taxTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              <Money
                amount={newTotals.taxTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {taxDiff !== 0 ? (
                <Money
                  amount={taxDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : (
                "—"
              )}
            </TotalsCell>
          </TotalsRow>
        )}

        <TotalsRow $highlight>
          <TotalsLabel>Grand Total</TotalsLabel>
          <TotalsCell $type="before">
            <Money
              amount={oldTotals.grandTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsCell>
          <TotalsCell $type="after">
            <Money
              amount={newTotals.grandTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsCell>
          <TotalsCell $type="diff">
            {grandTotalDiff !== 0 ? (
              <Money
                amount={grandTotalDiff}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            ) : (
              "—"
            )}
          </TotalsCell>
        </TotalsRow>
      </TotalsComparison>
    )
  }

  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  if (!order) {
    return null
  }

  if (isModificationFailed) {
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <Container>
          <Title>
            <div>
              <h2>Failed Modification shipping fees</h2>
            </div>
          </Title>
          id: {modification.id} <CopyButton string={modification.id} />
          {hasPaymentProvider && (
            <ProviderWrapper>
              <h4>Failed providers:</h4>
              <Provider>Payment</Provider>
            </ProviderWrapper>
          )}
          <CartItems>
            <Label style={{ marginTop: "16px" }}>Shipping Fees</Label>
            {renderShippingFeesTable()}

            <Reason reason={modification?.reason} getCustomCode={getCustomCode} />

            {renderTotalsComparison()}
          </CartItems>

          <ActionInfoBox showManual={false} />

          <Providers>
            {hasPaymentProvider && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <PaymentIcon />
                    <b>Payment - {modification.paymentProvider?.providerName}</b>
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(paymentAction.actionType)}>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Auto })
                      }
                    >
                      Auto
                    </li>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Manual })
                      }
                    >
                      Manual
                    </li>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Skip })
                      }
                    >
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
          </Providers>

          <ErrorMessages
            orderHistory={orderHistory.history}
            operationId={modification.id}
            operation={"SHIPPING_FEES_MODIFICATION"}
          />

          <ButtonWrapper>
            <SecondaryButton handleClick={restartOperation} loading={restartLoading}>
              Restart modification
            </SecondaryButton>
          </ButtonWrapper>
        </Container>
      </>
    )
  }

  return (
    <>
      <CartContainer>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <CartItems>
          <Label style={{ marginTop: "16px" }}>Shipping Fees</Label>
          {renderShippingFeesTable()}

          <Reason reason={modification?.reason} getCustomCode={getCustomCode} />

          {renderTotalsComparison()}
        </CartItems>
        <StartOperation
          operation={modification}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOperation}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default ShippingFeesModificationAction
